/**
 * Centralized configuration for all supported sample file types
 * Update this file to add or modify supported file extensions
 */

// Define all supported file extensions
// Multi-part extensions should be ordered longest first for proper matching
export const SAMPLE_FILE_EXTENSIONS = {
  // Thermo Fisher RAW files
  raw: '.raw',

  // Waters files
  lcd: '.lcd',

  // mzML/mzXML files
  mzml: '.mzml',
  mzxml: '.mzxml',

  // WIFF files (Sciex) - multi-part
  wiff_scan: '.wiff.scan',
  wiff_mtd: '.wiff.mtd',
  wiff: '.wiff',
  wiff_time_series: '.timeseries.data',

  // WIFF2 files (Sciex) - multi-part
  wiff2_scan: '.wiff2.scan',
  wiff2_mtd: '.wiff2.mtd',
  wiff2: '.wiff2',
} as const;

// Get all extensions as an array (for backwards compatibility)
export const sampleFileExtensions = Object.values(SAMPLE_FILE_EXTENSIONS);

// Multi-part extensions that need to be grouped together for zipping
// Order matters: longer extensions first to match correctly
export const multiPartExtensions = [
  SAMPLE_FILE_EXTENSIONS.wiff2_scan,
  SAMPLE_FILE_EXTENSIONS.wiff2_mtd,
  SAMPLE_FILE_EXTENSIONS.wiff2,
  SAMPLE_FILE_EXTENSIONS.wiff_scan,
  SAMPLE_FILE_EXTENSIONS.wiff_mtd,
  SAMPLE_FILE_EXTENSIONS.wiff,
  SAMPLE_FILE_EXTENSIONS.wiff_time_series,
] as const;

/**
 * Check if a filename has a supported extension
 */
export function hasSupportedExtension(filename: string): boolean {
  const lowerFilename = filename.toLowerCase();
  return sampleFileExtensions.some((ext) =>
    lowerFilename.endsWith(ext.toLowerCase())
  );
}

/**
 * Get the extension from a filename, handling multi-part extensions
 */
export function getFileExtension(filename: string): string | null {
  const lowerFilename = filename.toLowerCase();

  // Check multi-part extensions first (they're longer)
  for (const ext of multiPartExtensions) {
    if (lowerFilename.endsWith(ext.toLowerCase())) {
      return ext;
    }
  }

  // Check single-part extensions
  for (const ext of sampleFileExtensions) {
    if (lowerFilename.endsWith(ext.toLowerCase())) {
      return ext;
    }
  }

  return null;
}

/**
 * Remove extension from filename, handling multi-part extensions
 */
export function removeFileExtension(filename: string): string {
  const ext = getFileExtension(filename);
  if (ext) {
    return filename.slice(0, -ext.length);
  }

  // Fallback: remove last extension
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex > 0) {
    return filename.slice(0, lastDotIndex);
  }

  return filename;
}

/**
 * Extension priority configuration for multi-part file types
 * When multiple files need to be zipped, this determines which extension
 * should be used as the "dominant" extension in the ZIP filename.
 *
 * Example: For WIFF files with .wiff and .wiff.scan files,
 * we want the ZIP to be named "sample.wiff.zip" not "sample.scan.zip"
 */
export interface ExtensionPriorityGroup {
  /** All extensions that belong to this file type family */
  extensions: readonly string[];
  /** Priority order - first match wins (highest priority first) */
  priority: readonly string[];
}

export const EXTENSION_PRIORITY_GROUPS: Record<string, ExtensionPriorityGroup> =
  {
    wiff: {
      // WIFF file family includes .wiff, .wiff2, and their .scan/.mtd companions
      extensions: [
        SAMPLE_FILE_EXTENSIONS.wiff,
        SAMPLE_FILE_EXTENSIONS.wiff_scan,
        SAMPLE_FILE_EXTENSIONS.wiff_mtd,
        SAMPLE_FILE_EXTENSIONS.wiff2,
        SAMPLE_FILE_EXTENSIONS.wiff2_scan,
        SAMPLE_FILE_EXTENSIONS.wiff2_mtd,
        SAMPLE_FILE_EXTENSIONS.wiff_time_series,
      ],
      // Priority: prefer .wiff over .wiff2, never use .scan or .mtd
      priority: [SAMPLE_FILE_EXTENSIONS.wiff, SAMPLE_FILE_EXTENSIONS.wiff2],
    },
    // Add more priority groups here as needed for other multi-part file types
    // Example for future file types:
    // bruker: {
    //   extensions: ['.d', '.d.scan', '.d.info'],
    //   priority: ['.d'],
    // },
  } as const;
